/**
 * Authentication Service
 *
 * Handles user authentication, session management, and store synchronization.
 * Extracted from supabase.ts AuthService class.
 *
 * @see PHASE4B_ARCHITECTURE.md for service specifications
 */

import type { SupabaseClient, User, Session } from '@supabase/supabase-js';
import type { AuthStoreAdapter } from '@/services/adapters/store.adapters';
import { logSecurityEvent } from '@/config/security';
import { validateInput } from '@/utils/security-headers';

export class AuthService {
  constructor(
    private supabase: SupabaseClient,
    private authStore: AuthStoreAdapter
  ) {}

  /**
   * Sign up a new user
   */
  async signUp(email: string, password: string, userData?: { name?: string }) {
    try {
      // Validate inputs
      const emailValidation = validateInput(email, 255);
      const passwordValidation = validateInput(password, 128);

      if (!emailValidation.isValid) {
        logSecurityEvent('signup_invalid_email', { error: emailValidation.error });
        return { data: null, error: new Error(`Invalid email: ${emailValidation.error}`) };
      }

      if (!passwordValidation.isValid) {
        logSecurityEvent('signup_invalid_password', { error: passwordValidation.error });
        return { data: null, error: new Error(`Invalid password: ${passwordValidation.error}`) };
      }

      // Validate name if provided
      if (userData?.name) {
        const nameValidation = validateInput(userData.name, 100);
        if (!nameValidation.isValid) {
          logSecurityEvent('signup_invalid_name', { error: nameValidation.error });
          return { data: null, error: new Error(`Invalid name: ${nameValidation.error}`) };
        }
        userData.name = nameValidation.sanitized;
      }

      const { data, error } = await this.supabase.auth.signUp({
        email: emailValidation.sanitized,
        password: passwordValidation.sanitized,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/auth/login`,
        },
      });

      if (error) throw error;

      // Only sync to store when we have an actual session (email confirmation disabled).
      // When email confirmation is required, session is null — syncing would set
      // isAuthenticated=true with no real session, causing an infinite loading loop.
      if (data.user && data.session) {
        await this.syncUserToStore(data.user, data.session);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    }
  }

  /**
   * Sign in existing user
   */
  async signIn(email: string, password: string) {
    try {
      // Validate inputs
      const emailValidation = validateInput(email, 255);
      const passwordValidation = validateInput(password, 128);

      if (!emailValidation.isValid) {
        logSecurityEvent('signin_invalid_email', { error: emailValidation.error });
        return { data: null, error: new Error(`Invalid email: ${emailValidation.error}`) };
      }

      if (!passwordValidation.isValid) {
        logSecurityEvent('signin_invalid_password', { error: passwordValidation.error });
        return { data: null, error: new Error(`Invalid password: ${passwordValidation.error}`) };
      }

      const { data, error } = await this.supabase.auth.signInWithPassword({
        email: emailValidation.sanitized,
        password: passwordValidation.sanitized,
      });

      if (error) throw error;

      // Sync user to store
      if (data.user) {
        await this.syncUserToStore(data.user, data.session);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  }

  /**
   * Sign out current user
   */
  async signOut() {
    try {
      const { error } = await this.supabase.auth.signOut();
      if (error) throw error;

      // Clear all stores
      this.authStore.clearAuth();
      // Note: We might want to keep some learning data cached for offline use

      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error };
    }
  }

  /**
   * Reset user password
   */
  async resetPassword(email: string) {
    try {
      // Validate email input
      const emailValidation = validateInput(email, 255);
      if (!emailValidation.isValid) {
        logSecurityEvent('reset_password_invalid_email', { error: emailValidation.error });
        return { error: new Error(`Invalid email: ${emailValidation.error}`) };
      }

      // redirectTo MUST point at the dedicated set-new-password page so the
      // recovery link lands there (not the dashboard) — otherwise the recovery
      // session becomes a silent login. The URL must be allowlisted in each
      // Supabase project's Auth → URL Configuration or Supabase falls back to
      // the Site URL.
      const { error } = await this.supabase.auth.resetPasswordForEmail(emailValidation.sanitized, {
        redirectTo: `${window.location.origin}/auth/update-password`,
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Password reset error:', error);
      return { error };
    }
  }

  /**
   * Change password for the currently authenticated user.
   * Verifies the current password before applying the new one.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ error: Error | null }> {
    try {
      // Validate inputs
      const currentValidation = validateInput(currentPassword, 128);
      const newValidation = validateInput(newPassword, 128);

      if (!currentValidation.isValid) {
        return { error: new Error(`Invalid current password: ${currentValidation.error}`) };
      }

      if (!newValidation.isValid) {
        return { error: new Error(`Invalid new password: ${newValidation.error}`) };
      }

      // Get current user's email
      const {
        data: { user },
        error: getUserError,
      } = await this.supabase.auth.getUser();

      if (getUserError || !user?.email) {
        return { error: new Error('Unable to verify current user. Please sign in again.') };
      }

      // Verify current password by attempting to sign in
      const { error: verifyError } = await this.supabase.auth.signInWithPassword({
        email: user.email,
        password: currentValidation.sanitized,
      });

      if (verifyError) {
        return { error: new Error('Current password is incorrect') };
      }

      // Apply the new password
      const { error: updateError } = await this.supabase.auth.updateUser({
        password: newValidation.sanitized,
      });

      if (updateError) {
        return { error: updateError };
      }

      return { error: null };
    } catch (error) {
      console.error('Change password error:', error);
      return { error: error instanceof Error ? error : new Error('An unexpected error occurred') };
    }
  }

  /**
   * Permanently delete the current user's account.
   * Calls the delete-account Edge Function which uses the admin API
   * to remove the user and cascade-delete all associated data.
   */
  async deleteAccount(): Promise<{ error: Error | null }> {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        return { error: new Error('No active session') };
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
      const response = await fetch(`${supabaseUrl}/functions/v1/delete-account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const text = await response.text();
        let errorMessage = 'Failed to delete account';
        try {
          const data = JSON.parse(text);
          errorMessage = data.error || errorMessage;
        } catch {
          // Response was not JSON (e.g. HTML error page)
        }
        return { error: new Error(errorMessage) };
      }

      // Clear local state after successful deletion
      await this.supabase.auth.signOut();
      this.authStore.clearAuth();

      return { error: null };
    } catch (error) {
      console.error('Delete account failed:', error);
      return { error: error instanceof Error ? error : new Error('Failed to delete account') };
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(updates: { name?: string; avatar_url?: string; preferred_language?: string }) {
    try {
      const {
        data: { user },
        error: getUserError,
      } = await this.supabase.auth.getUser();
      if (getUserError || !user) throw getUserError || new Error('No user logged in');

      // Update user metadata through Supabase Auth
      const { data, error } = await this.supabase.auth.updateUser({
        data: {
          ...user.user_metadata,
          ...updates,
        },
      });

      if (error) throw error;

      // Update local store with new user data
      if (data.user) {
        await this.syncUserToStore(data.user, null);
      }

      return { data: data.user, error: null };
    } catch (error) {
      console.error('Profile update error:', error);
      return { data: null, error };
    }
  }

  /**
   * Sync user data to store
   */
  async syncUserToStore(user: User, session: Session | null) {
    try {
      // Set user data from auth metadata and profile
      const userData = {
        id: user.id,
        email: user.email || '',
        name: user.user_metadata?.name || user.user_metadata?.full_name || null,
        avatar_url: user.user_metadata?.avatar_url || null,
        preferred_language: user.user_metadata?.preferred_language || null,
        created_at: user.created_at,
        last_login: user.last_sign_in_at || new Date().toISOString(),
      };

      this.authStore.setUser(userData as any);
      this.authStore.setSession(session);
    } catch (error) {
      console.error('Error syncing user to store:', error);
      // Don't throw - let the app continue with whatever data we have
    }
  }
}
